import * as React from 'react';
import { TechnicalMetrics } from '../../utils/technicalAnalysis';
import './style.scss';

const recommendationLabels: Record<TechnicalMetrics['recommendation'], string> = {
    strong_buy: 'Strong Buy',
    buy: 'Buy',
    hold: 'Hold',
    sell: 'Sell',
    strong_sell: 'Strong Sell',
};

const recommendationSummaries: Record<TechnicalMetrics['recommendation'], string> = {
    strong_buy: 'Bullish technical signals clearly dominate. Trend, momentum, and supporting factors align for upside bias.',
    buy: 'More bullish than bearish signals are active. Conditions favor holding or adding exposure with normal risk controls.',
    hold: 'Bullish and bearish signals are roughly balanced. No strong edge either way — wait for clearer confirmation.',
    sell: 'Bearish signals outweigh bullish ones. Consider reducing exposure or tightening risk.',
    strong_sell: 'Bearish technical signals clearly dominate. Trend and momentum point against holding long exposure.',
};

const factorExplanations: Record<string, string> = {
    sma_golden_cross: 'The 50-day average is above the 200-day average — a common sign of a longer-term uptrend (golden cross).',
    sma_death_cross: 'The 50-day average is below the 200-day average — a common sign of a longer-term downtrend (death cross).',
    macd_bullish: 'MACD is above its signal line, suggesting positive momentum is building.',
    macd_bearish: 'MACD is below its signal line, suggesting momentum is weakening or turning down.',
    rsi_healthy: 'RSI is between 40 and 70 — strong enough to trend but not extremely overbought.',
    rsi_overbought: 'RSI is above 70 — price may be stretched and due for a pause or pullback.',
    rsi_weak: 'RSI is below 40 — momentum is weak, which often accompanies downtrends or heavy selling.',
    volume_up_day: 'Today\'s volume is well above average on an up day — buying interest is elevated.',
    volume_down_day: 'Today\'s volume is well above average on a down day — selling pressure is elevated.',
    uptrend_days: 'At least 3 of the last 10 sessions closed higher — short-term price action is positive.',
    downtrend_days: 'At least 3 of the last 10 sessions closed lower — short-term price action is negative.',
    volatility_limit: 'The latest daily move is below 30%, avoiding extreme one-day swings that can distort signals.',
};

interface RecommendationDetailModalProps {
    metrics: TechnicalMetrics;
    onClose: () => void;
}

export const RecommendationDetailModal: React.FC<RecommendationDetailModalProps> = ({ metrics, onClose }) => {
    const bullishFactors = metrics.scoreFactors.filter((factor) => factor.side === 'bullish');
    const bearishFactors = metrics.scoreFactors.filter((factor) => factor.side === 'bearish');
    const recommendationClass = metrics.recommendation.includes('buy')
        ? 'positive'
        : metrics.recommendation.includes('sell')
            ? 'negative'
            : 'neutral';

    React.useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    return (
        <div
            className='recommendation-modal-overlay'
            role='dialog'
            aria-modal='true'
            aria-labelledby='recommendation-modal-title'
            onClick={onClose}
        >
            <div className='recommendation-modal' onClick={(event) => event.stopPropagation()}>
                <div className='recommendation-modal-header'>
                    <div>
                        <p className='recommendation-modal-eyebrow'>Technical recommendation</p>
                        <h2 id='recommendation-modal-title' className={`recommendation-modal-title ${recommendationClass}`}>
                            {recommendationLabels[metrics.recommendation]}
                        </h2>
                    </div>
                    <button type='button' className='recommendation-modal-close' onClick={onClose} aria-label='Close'>
                        ×
                    </button>
                </div>

                <p className='recommendation-modal-summary'>
                    {recommendationSummaries[metrics.recommendation]}
                </p>

                <div className='recommendation-modal-scores'>
                    <div className='score-card'>
                        <span className='score-label'>Net score</span>
                        <span className={`score-value ${metrics.netScore >= 0 ? 'positive' : 'negative'}`}>
                            {metrics.netScore >= 0 ? '+' : ''}{metrics.netScore}
                        </span>
                    </div>
                    <div className='score-card'>
                        <span className='score-label'>Bullish</span>
                        <span className='score-value positive'>{metrics.bullishScore}</span>
                    </div>
                    <div className='score-card'>
                        <span className='score-label'>Bearish</span>
                        <span className='score-value negative'>{metrics.bearishScore}</span>
                    </div>
                </div>

                <div className='recommendation-modal-how'>
                    <h3>How the score works</h3>
                    <p>
                        Each bullish or bearish condition adds weighted points when it passes. Higher-importance signals
                        (trend and momentum) count more than short-term ones. Net score = bullish points − bearish points.
                    </p>
                    <ul>
                        <li><strong>Strong Buy</strong> — net ≥ +7</li>
                        <li><strong>Buy</strong> — net +3 to +6</li>
                        <li><strong>Hold</strong> — net −2 to +2</li>
                        <li><strong>Sell</strong> — net −5 to −3</li>
                        <li><strong>Strong Sell</strong> — net ≤ −6</li>
                    </ul>
                </div>

                <div className='recommendation-modal-section'>
                    <h3>Bullish signals</h3>
                    <div className='signal-list'>
                        {bullishFactors.map((factor) => (
                            <div key={factor.key} className={`signal-row ${factor.passed ? 'active bullish' : 'inactive'}`}>
                                <div className='signal-row-header'>
                                    <span className='signal-status'>{factor.passed ? 'Active' : 'Not met'}</span>
                                    <span className='signal-points'>+{factor.points} pts</span>
                                </div>
                                <p className='signal-label'>{factor.label}</p>
                                <p className='signal-detail'>{factorExplanations[factor.key]}</p>
                            </div>
                        ))}
                    </div>
                </div>

                <div className='recommendation-modal-section'>
                    <h3>Bearish signals</h3>
                    <div className='signal-list'>
                        {bearishFactors.map((factor) => (
                            <div key={factor.key} className={`signal-row ${factor.passed ? 'active bearish' : 'inactive'}`}>
                                <div className='signal-row-header'>
                                    <span className='signal-status'>{factor.passed ? 'Active' : 'Not met'}</span>
                                    <span className='signal-points'>+{factor.points} pts</span>
                                </div>
                                <p className='signal-label'>{factor.label}</p>
                                <p className='signal-detail'>{factorExplanations[factor.key]}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};
